import { useCallback, useMemo, useState } from 'react'
import { routes } from '@/shared/constants'
import { PAYABLE_STATUSES } from '@/features/billing/constants/billingStatusFilters'
import { useMyBusinesses } from '@/features/businesses/hooks/useMyBusinesses'
import { useMyBillings } from '@/features/billing/hooks/useMyBillings'
import { useOrgTickets } from '@/features/tickets/hooks/useOrgTickets'
import { useOrgTicketScope } from '@/features/tickets/hooks/useOrgTicketScope'
import type { AppNotification } from './types'
import { relativeTime } from './relativeTime'

const SEEN_KEY = 'portal:notifications:seen'
const MAX_ITEMS = 12
const RECENT_DAYS = 30
const PER_SOURCE = 5

/** Notificación sin el estado derivado (`read`/`time`), tal cual la produce el builder. */
export type RawNotification = Omit<AppNotification, 'read' | 'time'>

interface BusinessSource {
  id: string
  legalName: string
  createdAt?: string | null
  isActive?: boolean | null
}
interface InvoiceSource {
  id: number
  invoiceNumber: string
  status: string
  registeredAt?: string | null
}
interface TicketSource {
  code: string
  title: string
  createdAt?: string | null
  fixConfirmed?: boolean | null
  status?: { code: string; name: string } | null
}

export interface NotificationSources {
  businesses: BusinessSource[]
  billings: InvoiceSource[]
  tickets: TicketSource[]
}

const parseTs = (value?: string | null): number | null => {
  if (!value) return null
  const ts = Date.parse(value)
  return Number.isNaN(ts) ? null : ts
}

/**
 * Construye la lista de notificaciones a partir de datos REALES del portal
 * (negocios, facturas, tickets). Pura y determinista dado `now` → testeable sin
 * montar hooks. No incluye `read`/`time`: eso lo resuelve el hook.
 */
export function buildNotifications(
  now: number,
  { businesses, billings, tickets }: NotificationSources,
): RawNotification[] {
  const out: RawNotification[] = []
  const recentCutoff = now - RECENT_DAYS * 86_400_000

  for (const biz of businesses) {
    const created = parseTs(biz.createdAt)
    if (created != null && created >= recentCutoff) {
      out.push({
        id: `business:created:${biz.id}`,
        type: 'business_change',
        title: 'Negocio creado',
        description: `“${biz.legalName}” se creó en tu organización.`,
        timestamp: created,
        href: routes.app.businesses.detail(biz.id),
      })
    }
    if (biz.isActive === false) {
      out.push({
        id: `business:pending:${biz.id}`,
        type: 'business_change',
        title: 'Negocio pendiente de activación',
        description: `“${biz.legalName}” está pendiente de activación.`,
        timestamp: created ?? now,
        href: routes.app.businesses.detail(biz.id),
      })
    }
  }

  for (const inv of billings) {
    const ts = parseTs(inv.registeredAt) ?? now
    const payable = PAYABLE_STATUSES.has((inv.status ?? '').toUpperCase())
    out.push(
      payable
        ? {
            id: `invoice:pending:${inv.id}`,
            type: 'invoice_pending',
            title: 'Factura pendiente de pago',
            description: `La factura ${inv.invoiceNumber} está pendiente de pago.`,
            timestamp: ts,
            href: routes.app.billing.detail(String(inv.id)),
          }
        : {
            id: `invoice:${inv.id}`,
            type: 'invoice_generated',
            title: 'Nueva factura',
            description: `Se generó la factura ${inv.invoiceNumber}.`,
            timestamp: ts,
            href: routes.app.billing.detail(String(inv.id)),
          },
    )
  }

  for (const t of tickets) {
    const ts = parseTs(t.createdAt) ?? now
    const statusCode = t.status?.code ?? ''
    const statusName = t.status?.name ?? ''
    out.push({
      id: `ticket:${t.code}:${statusCode}${t.fixConfirmed ? ':fixed' : ''}`,
      type: 'ticket_update',
      title: t.fixConfirmed ? 'Corrección confirmada' : `Ticket ${t.code}`,
      description: t.fixConfirmed
        ? `Confirmaste la corrección de “${t.title}”.`
        : `“${t.title}”${statusName ? ` — ${statusName}` : ''}.`,
      timestamp: ts,
      href: routes.app.tickets.detail(t.code),
    })
  }

  return out.sort((a, b) => b.timestamp - a.timestamp).slice(0, MAX_ITEMS)
}

function loadSeen(): Set<string> {
  try {
    const raw = localStorage.getItem(SEEN_KEY)
    if (!raw) return new Set()
    const parsed: unknown = JSON.parse(raw)
    return Array.isArray(parsed) ? new Set(parsed.filter((x): x is string => typeof x === 'string')) : new Set()
  } catch {
    return new Set()
  }
}

function persistSeen(seen: Set<string>): void {
  try {
    localStorage.setItem(SEEN_KEY, JSON.stringify([...seen]))
  } catch {
    // Sin localStorage (modo privado/cuota): el estado vive solo en memoria.
  }
}

export interface UseNotificationsResult {
  items: AppNotification[]
  unread: number
  markAllRead: () => void
  markRead: (id: string) => void
  loading: boolean
}

/**
 * Notificaciones del portal admin a partir de datos REALES ya consultables:
 * negocios (creados + pendientes de activación), facturas (nuevas + pendientes)
 * y tickets (estado + corrección confirmada). Se fusionan y ordenan por fecha.
 * El estado "leído" se persiste en `localStorage` (no hay backend de
 * notificaciones aún). Tolerante a fallos: cada fuente se guarda por alcance y
 * una caída no rompe la campana.
 */
export function useNotifications(): UseNotificationsResult {
  const {
    businesses,
    totalItems: businessCount,
    loading: loadingBusinesses,
  } = useMyBusinesses({ take: PER_SOURCE, skip: 0 })
  const hasBusinesses = businessCount > 0

  const { billings, loading: loadingBillings } = useMyBillings({
    take: PER_SOURCE,
    skip: 0,
    disabled: !hasBusinesses,
  })

  const { hasScope } = useOrgTicketScope()
  const { tickets, loading: loadingTickets } = useOrgTickets({
    page: 1,
    perPage: PER_SOURCE,
    enabled: hasScope,
  })

  // El reloj se lee UNA vez al montar (no en cada render → función pura): la
  // campana se abre puntualmente, no necesita recalcular la recencia en vivo.
  const [now] = useState(() => Date.now())

  const raw = useMemo(
    () =>
      buildNotifications(now, {
        businesses,
        billings,
        tickets,
      }),
    [now, businesses, billings, tickets],
  )

  const [seen, setSeen] = useState<Set<string>>(loadSeen)

  const items = useMemo<AppNotification[]>(
    () =>
      raw.map((n) => ({
        ...n,
        read: seen.has(n.id),
        time: relativeTime(n.timestamp, now),
      })),
    [raw, seen, now],
  )

  const unread = items.reduce((acc, n) => acc + (n.read ? 0 : 1), 0)

  const markAllRead = useCallback(() => {
    setSeen((prev) => {
      const next = new Set(prev)
      raw.forEach((n) => next.add(n.id))
      persistSeen(next)
      return next
    })
  }, [raw])

  const markRead = useCallback((id: string) => {
    setSeen((prev) => {
      if (prev.has(id)) return prev
      const next = new Set(prev)
      next.add(id)
      persistSeen(next)
      return next
    })
  }, [])

  return {
    items,
    unread,
    markAllRead,
    markRead,
    loading: loadingBusinesses || loadingBillings || loadingTickets,
  }
}
